"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { useDialogBodyPointerEventsReset } from "hooks/useDialogBodyPointerEventsReset";

import { keepOpenOnCookieConsentInteraction } from "../../../../../products/muggleTestV0/lib/cookieConsentInteraction";
import { cn } from "../../../../../products/muggleTestV0/lib/utils";

import { ModalCloseButton } from "./modal-close-button";

/**
 * Dialog root. Also clears the `pointer-events: none` Radix can leave on `<body>` when its own
 * cleanup is interrupted by a re-render or navigation firing in the same tick as the close (e.g. a
 * save handler that refetches and toasts), which would otherwise freeze the whole page.
 *
 * The clearing itself is shared with the campaign's own dialog, which hits the same Radix defect.
 */
const Dialog = ({ open, onOpenChange, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  const { onClosed } = useDialogBodyPointerEventsReset({ isOpen: open });

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
      if (!next) onClosed();
    },
    [onOpenChange, onClosed]
  );

  return <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Available dialog sizes.
 * - "sm": Small dialogs (~512px) - for simple forms, confirmations
 * - "md": Medium dialogs (~672px) - for moderate content
 * - "lg": Large dialogs (~896px) - for complex forms
 * - "xl": Extra large dialogs (~1024px) - for detailed views
 * - "2xl": 2X large dialogs (~1152px) - for rich content
 * - "full": Full-width dialogs (~1280px) - for complex multi-panel layouts
 */
type DialogSize = "sm" | "md" | "lg" | "xl" | "2xl" | "full";

/**
 * Size class mappings for the fullscreen variant.
 * Includes: mobile width, desktop max-width, max-height, flex layout, no padding.
 */
const FULLSCREEN_SIZE_CLASSES: Record<DialogSize, string> = {
  sm: "w-[96vw] sm:max-w-lg max-h-[90svh] p-0 flex flex-col overflow-hidden",
  md: "w-[96vw] sm:max-w-2xl max-h-[90svh] p-0 flex flex-col overflow-hidden",
  lg: "w-[96vw] sm:max-w-4xl max-h-[90svh] p-0 flex flex-col overflow-hidden",
  xl: "w-[96vw] sm:max-w-5xl max-h-[90svh] p-0 flex flex-col overflow-hidden",
  "2xl": "w-[96vw] sm:max-w-6xl max-h-[90svh] p-0 flex flex-col overflow-hidden",
  full: "w-[96vw] sm:max-w-7xl max-h-[90svh] p-0 flex flex-col overflow-hidden",
};

/**
 * Size class mappings for the default variant.
 * Only overrides width - default variant handles its own padding and scroll.
 */
const DEFAULT_SIZE_CLASSES: Record<DialogSize, string> = {
  sm: "w-[96vw] sm:max-w-lg",
  md: "w-[96vw] sm:max-w-2xl",
  lg: "w-[96vw] sm:max-w-4xl",
  xl: "w-[96vw] sm:max-w-5xl",
  "2xl": "w-[96vw] sm:max-w-6xl",
  full: "w-[96vw] sm:max-w-7xl",
};

/** Props for DialogContent with variant and size support. */
interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Dialog variant.
   * - "default": Mobile-friendly bottom sheet with scrollable content wrapper
   * - "fullscreen": No content wrapper, allows custom layouts (e.g., side panels)
   */
  variant?: "default" | "fullscreen";
  /**
   * Dialog size preset. When provided, applies standard width/height/layout classes.
   * For fullscreen variant: includes w-[96vw], sm:max-w-*, max-h-[90svh], p-0, flex flex-col.
   * For default variant: includes w-[96vw], sm:max-w-* (overrides default sm:max-w-lg).
   * Custom className can still override these if needed.
   */
  size?: DialogSize;
  /**
   * Optional actions rendered at the top-right of the modal, to the left of the close button.
   * Use this slot for contextual controls like "Need Support".
   */
  topRightActions?: React.ReactNode;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, variant = "default", size, ...props }, ref) => {
  const { topRightActions, onInteractOutside, ...dialogContentProps } = props;
  const sizeClasses = size
    ? variant === "fullscreen"
      ? FULLSCREEN_SIZE_CLASSES[size]
      : DEFAULT_SIZE_CLASSES[size]
    : undefined;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          variant === "fullscreen"
            ? [
              // Fullscreen variant: Mobile bottom sheet, desktop centered (no content wrapper)
              // Mobile: bottom sheet, centred when a size narrows it below full width
              "fixed inset-x-0 bottom-0 z-50 mx-auto w-full border-t bg-background shadow-lg duration-200 rounded-t-xl",
              // Desktop: centered dialog with no restrictive max-width
              "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:border",
              // Animations
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              // Mobile: slide from bottom
              "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
              // Desktop: centered zoom (overrides mobile slide)
              "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%] sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
            ]
            : [
              // Default variant: Mobile bottom sheet, desktop centered dialog (with content wrapper)
              // Mobile: bottom sheet, centred when a size narrows it below full width
              "fixed inset-x-0 bottom-0 z-50 mx-auto flex flex-col w-full max-h-[85vh] border-t bg-background shadow-lg duration-200 rounded-t-xl",
              // Desktop: centered dialog
              "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:w-[calc(100%-2rem)] sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-h-[calc(100vh-2rem)] sm:rounded-lg sm:border",
              // Animations
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              // Mobile: slide from bottom
              "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
              // Desktop: centered zoom (overrides mobile slide)
              "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%] sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
            ],
          sizeClasses,
          className
        )}
        onInteractOutside={keepOpenOnCookieConsentInteraction(onInteractOutside)}
        {...dialogContentProps}
      >
      {variant === "fullscreen" ? (
        <>
          {/* Mobile drag handle indicator */}
          <div className="sm:hidden mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30 absolute top-0 left-1/2 -translate-x-1/2 z-10" />
          {children}
          <div className="absolute right-4 top-4 z-20 flex items-start gap-3">
            {topRightActions}
            <ModalCloseButton className="static right-auto top-auto" />
          </div>
        </>
      ) : (
        <>
          {/* Mobile drag handle indicator */}
          <div className="sm:hidden mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30" />
          <div className="overflow-y-auto flex-1 min-h-0 p-4 sm:p-6 space-y-4">{children}</div>
          <div className="absolute right-4 top-4 z-20 flex items-start gap-3">
            {topRightActions}
            <ModalCloseButton className="static right-auto top-auto" />
          </div>
        </>
      )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * DialogHeader component - container for modal header content.
 * Supports two-column layout when using DialogTitleGroup and DialogActions as children.
 */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left sticky top-0 bg-background z-10 pb-4 pr-12", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

/**
 * DialogTitleGroup component - container for title and description.
 * Use inside DialogHeader for proper two-column layout. Takes the full header width on mobile
 * (where the header stacks) and at most 70% of it from `sm` up, where the actions sit beside it.
 * `min-w-0` lets long titles wrap instead of pushing the actions column off screen.
 */
const DialogTitleGroup = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex min-w-0 flex-1 flex-col gap-1 max-w-full sm:max-w-[70%]", className)}
    {...props}
  />
);
DialogTitleGroup.displayName = "DialogTitleGroup";

/**
 * DialogActions component - container for action buttons in the header.
 * Use inside DialogHeader for proper two-column layout. Content is left-aligned within the column.
 */
const DialogActions = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex items-center gap-2 flex-shrink-0", className)}
    {...props}
  />
);
DialogActions.displayName = "DialogActions";

const DialogFooter = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTitleGroup,
  DialogTrigger,
  ModalCloseButton,
};

export type { DialogSize };
